// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';

const FilterPersistenceContext = createContext();
const FILTER_STORAGE_KEY = 'resourcesTableFilters';

// Default filter state
const DEFAULT_FILTERS = {
    viewMode: 'default',
    filterText: '',
    excludePrefixes: '',
    keywordFilter: '',
    hideAutoGenerated: true,
    currentPageIndex: 1,
    pageSize: 20,
};

const getInitialFilters = () => {
    // In test environment, always start with default filters
    const isNodeTest = typeof process !== 'undefined' && process.env?.NODE_ENV === 'test';
    const isViteTest = typeof globalThis !== 'undefined' && 
                      globalThis.__vite_ssr_import_meta__?.env?.MODE === 'test';
    
    if (isNodeTest || isViteTest) {
        return DEFAULT_FILTERS;
    }
    
    try {
        const stored = localStorage.getItem(FILTER_STORAGE_KEY);
        if (stored) {
            const parsedFilters = JSON.parse(stored);
            return validateAndSanitizeFilters(parsedFilters);
        }
    } catch (error) {
        // Handle localStorage access errors or JSON parsing errors
        console.warn('Failed to load filters from localStorage:', error);
    }
    
    return DEFAULT_FILTERS;
};

// Helper function to validate and sanitize stored filter data
const validateAndSanitizeFilters = (storedFilters) => {
    if (!storedFilters || typeof storedFilters !== 'object') {
        return DEFAULT_FILTERS;
    }

    const validViewModes = ['all', 'default', 'advanced'];
    const validPageSizes = [10, 20, 25, 50, 100, 250, 500];

    return {
        viewMode: validViewModes.includes(storedFilters.viewMode) ? storedFilters.viewMode : DEFAULT_FILTERS.viewMode,
        filterText: typeof storedFilters.filterText === 'string' ? storedFilters.filterText : DEFAULT_FILTERS.filterText,
        excludePrefixes: typeof storedFilters.excludePrefixes === 'string' ? storedFilters.excludePrefixes : DEFAULT_FILTERS.excludePrefixes,
        keywordFilter: typeof storedFilters.keywordFilter === 'string' ? storedFilters.keywordFilter : DEFAULT_FILTERS.keywordFilter,
        hideAutoGenerated: typeof storedFilters.hideAutoGenerated === 'boolean' ? storedFilters.hideAutoGenerated : DEFAULT_FILTERS.hideAutoGenerated,
        currentPageIndex: typeof storedFilters.currentPageIndex === 'number' && storedFilters.currentPageIndex > 0 ? storedFilters.currentPageIndex : DEFAULT_FILTERS.currentPageIndex,
        pageSize: validPageSizes.includes(storedFilters.pageSize) ? storedFilters.pageSize : DEFAULT_FILTERS.pageSize,
    };
};

export const useFilterPersistence = () => {
    const context = useContext(FilterPersistenceContext);
    if (!context) {
        throw new Error('useFilterPersistence must be used within a FilterPersistenceProvider');
    }
    return context;
};

export const FilterPersistenceProvider = ({ children }) => {
    // Initialize from localStorage if available, otherwise use defaults
    const [filters, setFilters] = useState(getInitialFilters);

    // Debounced save to localStorage to avoid excessive writes
    const saveTimeoutRef = React.useRef(null);
    
    useEffect(() => {
        // Clear any existing timeout
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        // Debounce the localStorage write by 500ms
        saveTimeoutRef.current = setTimeout(() => {
            try {
                localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
            } catch (error) {
                // Handle localStorage write errors (e.g., quota exceeded, private browsing)
                console.warn('Failed to save filters to localStorage:', error);
            }
        }, 500);

        // Cleanup timeout on unmount
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [filters]);

    const updateFilter = useCallback((key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    }, []);

    const updateFilters = useCallback((updates) => {
        setFilters(prev => ({
            ...prev,
            ...updates
        }));
    }, []);

    const resetFilters = useCallback(() => {
        setFilters(DEFAULT_FILTERS);
    }, []);

    const resetPagination = useCallback(() => {
        setFilters(prev => ({
            ...prev,
            currentPageIndex: 1
        }));
    }, []);

    const contextValue = useMemo(() => ({
        filters,
        updateFilter,
        updateFilters,
        resetFilters,
        resetPagination,
        // Individual filter accessors for convenience
        viewMode: filters.viewMode,
        filterText: filters.filterText,
        excludePrefixes: filters.excludePrefixes,
        keywordFilter: filters.keywordFilter,
        hideAutoGenerated: filters.hideAutoGenerated,
        currentPageIndex: filters.currentPageIndex,
        pageSize: filters.pageSize,
    }), [filters, updateFilter, updateFilters, resetFilters, resetPagination]);

    return (
        <FilterPersistenceContext.Provider value={contextValue}>
            {children}
        </FilterPersistenceContext.Provider>
    );
};
